import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import DOMPurify from "dompurify";
import { ArrowLeft, ExternalLink, Clock, Calendar, User, Pencil, ShieldCheck, Film } from "lucide-react";
import { api, SITE_URL } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { AdSlot } from "@/components/AdSlot";
import { SocialShareBar } from "@/components/SocialLinks";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";

DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (node.tagName === "A") {
    node.setAttribute("target", "_blank");
    node.setAttribute("rel", "nofollow noopener noreferrer");
  }
});

export default function ArticleDetail() {
  const { slug } = useParams();
  const { lang, t } = useLang();
  const { user } = useAuth();
  const isAdminOrEditor = user?.role === "admin" || user?.role === "editor";

  const [article, setArticle] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/articles/slug/${slug}`);
      setArticle(data);
      window.scrollTo(0, 0);
    } catch {
      setNotFound(true);
    }
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex flex-col">
        <Navbar />
        <div className="pt-36 pb-20 text-center px-6 max-w-md mx-auto my-auto">
          <p className="text-xl font-bold text-zinc-200">{t("articleNotFound")}</p>
          <p className="text-sm text-zinc-400 mt-2">La publicación que buscas no existe o ha sido despublicada.</p>
          <Link to="/revista" className="mt-6 inline-block bg-[#E11D48] text-white px-6 py-2.5 rounded-full text-xs font-semibold hover:bg-[#BE123C] transition-colors">
            Volver a la Revista
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050505]">
        <div className="w-10 h-10 border-2 border-[#E11D48] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Localized field helper
  const locTitle = (lang === "en" && article.title_en) || (lang === "fr" && article.title_fr) || article.title;
  const locExcerpt = (lang === "en" && article.excerpt_en) || (lang === "fr" && article.excerpt_fr) || article.excerpt;
  const locContent = (lang === "en" && article.content_en) || (lang === "fr" && article.content_fr) || article.content;
  const activeSlug = (lang === "en" && article.slug_en) || (lang === "fr" && article.slug_fr) || article.slug;

  const url = `${SITE_URL}/revista/${activeSlug}${lang !== "es" ? `?lang=${lang}` : ""}`;
  const cleanHtml = DOMPurify.sanitize(locContent || "", { ADD_ATTR: ["target", "rel"] });

  // Calculate read time
  const words = (locContent || "").replace(/<[^>]+>/g, "").split(/\s+/).length;
  const readTime = `${Math.max(3, Math.ceil(words / 200))} ${t("minRead")}`;

  const coverImgUrl = article.cover_url
    ? (article.cover_url.startsWith("http") ? article.cover_url : `${SITE_URL}${article.cover_url}`)
    : `${SITE_URL}/og-image.png`;

  const newsArticleSchema = {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": url
    },
    "headline": locTitle,
    "description": (locExcerpt || locTitle).slice(0, 200),
    "image": [coverImgUrl],
    "datePublished": article.published_at || article.created_at,
    "dateModified": article.updated_at || article.published_at || article.created_at,
    "author": {
      "@type": "Person",
      "name": article.author_name || "Redacción La Caja LGBT",
      "jobTitle": "Redactor Especializado en Cine",
      "worksFor": {
        "@type": "Organization",
        "name": "La Caja LGBT"
      }
    },
    "publisher": {
      "@type": "NewsMediaOrganization",
      "name": "La Caja LGBT",
      "url": SITE_URL,
      "logo": {
        "@type": "ImageObject",
        "url": `${SITE_URL}/logo.png`
      }
    },
    "isPartOf": {
      "@type": "Periodical",
      "name": "Revista La Caja LGBT",
      "url": `${SITE_URL}/revista`
    },
    "articleSection": article.category || "Crítica",
    "inLanguage": lang
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": t("home"),
        "item": SITE_URL
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": t("magazine"),
        "item": `${SITE_URL}/revista`
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": article.category || "General",
        "item": `${SITE_URL}/revista`
      },
      {
        "@type": "ListItem",
        "position": 4,
        "name": locTitle,
        "item": url
      }
    ]
  };

  return (
    <div className="min-h-screen bg-[#050505] text-zinc-100 flex flex-col" data-testid="article-detail">
      <Helmet>
        <title>{`${locTitle} — ${t("magazine")}`}</title>
        <meta name="description" content={(locExcerpt || locTitle).slice(0, 160)} />
        <link rel="canonical" href={url} />

        {/* Multilingual Hreflang Tags */}
        <link rel="alternate" hrefLang="es" href={`${SITE_URL}/revista/${article.slug}`} />
        <link rel="alternate" hrefLang="en" href={`${SITE_URL}/revista/${article.slug_en || article.slug}?lang=en`} />
        <link rel="alternate" hrefLang="fr" href={`${SITE_URL}/revista/${article.slug_fr || article.slug}?lang=fr`} />
        <link rel="alternate" hrefLang="x-default" href={`${SITE_URL}/revista/${article.slug}`} />

        {/* OpenGraph Article Metadata */}
        <meta property="og:title" content={locTitle} />
        <meta property="og:description" content={(locExcerpt || "").slice(0, 160)} />
        <meta property="og:image" content={coverImgUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={url} />
        <meta property="og:site_name" content="Revista La Caja LGBT" />
        {article.published_at && <meta property="article:published_time" content={article.published_at} />}
        {article.updated_at && <meta property="article:modified_time" content={article.updated_at} />}
        <meta property="article:section" content={article.category || "Crítica"} />
        <meta property="article:author" content={article.author_name || "Redacción La Caja LGBT"} />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={locTitle} />
        <meta name="twitter:description" content={(locExcerpt || "").slice(0, 160)} />
        <meta name="twitter:image" content={coverImgUrl} />

        {/* Structured Data JSON-LD */}
        <script type="application/ld+json">{JSON.stringify(newsArticleSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <Navbar />

      {/* Hero Cover Header / Video Banner */}
      <div className="relative bg-black overflow-hidden">
        {article.video_url ? (
          <div className="aspect-video w-full max-w-5xl mx-auto mt-20 sm:mt-24 rounded-2xl overflow-hidden shadow-2xl border border-white/10">
            <iframe
              src={`${article.video_url}${article.video_url.includes("?") ? "&" : "?"}autoplay=0`}
              title={locTitle}
              className="w-full h-full border-0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        ) : (
          <div className="relative h-[45vh] sm:h-[55vh] md:h-[60vh] bg-black">
            {article.cover_url ? (
              <img
                src={article.cover_url}
                alt={locTitle}
                className="w-full h-full object-cover opacity-60 scale-105"
              />
            ) : (
              <div className="w-full h-full bg-zinc-900" />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/60 to-transparent" />
          </div>
        )}
      </div>

      {/* Main Content Article Body Container */}
      <main className={`px-4 sm:px-6 md:px-12 ${article.video_url ? "mt-8" : "-mt-32 sm:-mt-40"} relative z-10 max-w-4xl mx-auto w-full flex-1`}>
        {/* Visual Breadcrumb Navigation & Admin Edit Bar */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-xs text-zinc-400 bg-black/80 backdrop-blur-md border border-white/15 px-4 py-2 rounded-full shadow-lg">
            <Link to="/" className="hover:text-white transition-colors">{t("home")}</Link>
            <span>/</span>
            <Link to="/revista" className="hover:text-white transition-colors">{t("magazine")}</Link>
            <span>/</span>
            <span className="text-[#E11D48] font-semibold">{article.category}</span>
          </nav>

          {isAdminOrEditor && (
            <Link
              to="/revista-editor"
              className="inline-flex items-center gap-1.5 bg-[#E11D48] text-white px-4 py-2 rounded-full text-xs font-semibold hover:bg-[#BE123C] transition-all shadow-lg"
              data-testid="article-admin-edit"
            >
              <Pencil size={14} /> Administrar / Editar artículo
            </Link>
          )}
        </div>

        {/* Article Meta Header Card */}
        <div className="bg-zinc-900/90 border border-white/15 rounded-2xl p-6 sm:p-8 md:p-10 shadow-2xl backdrop-blur-xl">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span className="text-[11px] font-extrabold uppercase tracking-widest bg-[#E11D48] text-white px-3 py-1 rounded-full shadow-md">
              {article.category}
            </span>
            <span className="text-xs text-zinc-400 font-medium flex items-center gap-1 ml-2">
              <Clock size={13} className="text-[#E11D48]" /> {readTime}
            </span>
          </div>

          <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-white leading-tight">
            {locTitle}
          </h1>

          {locExcerpt && (
            <p className="text-base sm:text-lg text-zinc-300 font-normal mt-4 leading-relaxed border-l-2 border-[#E11D48] pl-4 italic">
              {locExcerpt}
            </p>
          )}

          {/* Author Details Bar */}
          <div className="mt-6 pt-6 border-t border-white/10 flex flex-wrap items-center justify-between gap-4 text-xs text-zinc-400">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#E11D48]/20 border border-[#E11D48]/40 flex items-center justify-center text-[#E11D48] font-bold uppercase text-sm">
                {(article.author_name || "E")[0]}
              </div>
              <div>
                <p className="text-sm font-semibold text-white">{article.author_name || "Redacción La Caja"}</p>
                <p className="text-[11px] text-zinc-400">Redactor Especializado en Cine</p>
              </div>
            </div>

            {article.published_at && (
              <div className="flex items-center gap-1.5 text-zinc-400">
                <Calendar size={14} className="text-zinc-500" />
                <span>{new Date(article.published_at).toLocaleDateString(lang === "en" ? "en-US" : lang === "fr" ? "fr-FR" : "es-ES", { day: "numeric", month: "long", year: "numeric" })}</span>
              </div>
            )}
          </div>

          {/* Compartir Artículo en Redes Sociales */}
          <div className="mt-6">
            <SocialShareBar title={locTitle} description={locExcerpt} url={url} />
          </div>
        </div>

        {/* HTML Rendered Content Body */}
        <div
          className="article-content mt-10 text-zinc-200 text-base sm:text-lg leading-relaxed font-normal space-y-6"
          data-testid="article-body"
          dangerouslySetInnerHTML={{ __html: cleanHtml }}
        />

        {/* RELATED CATALOG TITLES */}
        {article.related_movies?.length > 0 && (
          <div className="mt-14 pt-8 border-t border-white/10">
            <h2 className="font-heading text-2xl font-bold text-white mb-6 flex items-center gap-2">
              <Film size={20} className="text-[#E11D48]" /> Títulos Relacionados en el Catálogo
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {article.related_movies.map((m) => (
                <Link
                  key={m.id}
                  to={`/titulo/${m.slug}`}
                  data-testid={`related-movie-${m.id}`}
                  className="group bg-zinc-900 border border-white/10 hover:border-[#E11D48]/50 rounded-xl overflow-hidden transition-all duration-300 hover:-translate-y-1 shadow-md"
                >
                  <div className="aspect-[2/3] bg-zinc-950 overflow-hidden relative" style={{ aspectRatio: "2 / 3" }}>
                    {m.poster_url ? (
                      <img
                        src={m.poster_url}
                        alt={m.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-xs text-zinc-600 p-2 text-center">
                        {m.title}
                      </div>
                    )}
                  </div>
                  <div className="p-3">
                    <p className="text-xs sm:text-sm font-semibold text-zinc-100 truncate group-hover:text-[#E11D48] transition-colors">
                      {m.title}
                    </p>
                    {m.year && <p className="text-[11px] text-zinc-500 mt-0.5">{m.year}</p>}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* EXTERNAL LINKS */}
        {article.related_links?.length > 0 && (
          <div className="mt-10 pt-6 border-t border-white/10">
            <h3 className="font-heading text-xl font-bold text-white mb-4">Enlaces y Fuentes Externas</h3>
            <ul className="space-y-2">
              {article.related_links.map((l, i) => (
                <li key={i}>
                  <a
                    href={l.url}
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    data-testid={`related-link-${i}`}
                    className="inline-flex items-center gap-2 text-sm text-[#E11D48] hover:text-[#BE123C] hover:underline font-medium transition-colors"
                  >
                    <ExternalLink size={15} /> {l.label || l.url}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="my-12"><AdSlot slot="9813456583" /></div>
      </main>

      <Footer />
    </div>
  );
}
